import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCredit } from '@/hooks/useCredit';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import {
  buildCustomerQrData,
  buildStatement,
  formatDate,
  formatDateTime,
  formatMoney,
  nextDueDate,
  TX_TYPE_META,
  type CreditTxType,
} from '@/lib/credit';
import TransactionModal from '../components/TransactionModal';
import CustomerFormModal from '../components/CustomerFormModal';
import CredentialsModal from '../components/CredentialsModal';
import QrModal from '../components/QrModal';
import ClientZifekActions from '@/pages/dashboard/apps/customers-manager/detail/components/ClientZifekActions';
import ClientBalanceCards from '@/components/feature/ClientBalanceCards';
import StoreTransferHistory from '@/components/feature/StoreTransferHistory';

/** Échappe le texte injecté dans la fenêtre d'impression. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default function CreditCustomerPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const credit = useCredit();
  const { profile } = useCompanyProfile();
  const { customers, currency, loading, error, reload, reloadSilent } = credit;
  const customerId = Number(id);

  const [txModal, setTxModal] = useState<{ mode: CreditTxType; reversesId?: number } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [credsOpen, setCredsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const customer = useMemo(() => customers.find((c) => c.id === customerId) || null, [customers, customerId]);
  const transactions = useMemo(() => credit.transactionsFor(customerId), [credit, customerId]);
  const balance = credit.balanceFor(customerId);

  useEffect(() => {
    if (!loading && customer && searchParams.get('statement') === '1') setStatementOpen(true);
  }, [loading, customer, searchParams]);

  const lastPayment = useMemo(
    () => transactions.find((tx) => tx.type === 'payment') || null,
    [transactions]
  );
  const dueDate = customer ? nextDueDate(customer.usual_payment_date) : null;
  const available = customer?.credit_limit != null ? Math.max(0, customer.credit_limit - balance) : null;

  const statementText = customer
    ? buildStatement({
        merchantName: profile.company_name || 'Mon commerce',
        customerName: customer.full_name,
        currency,
        balance,
        creditLimit: customer.credit_limit,
        transactions,
      })
    : '';

  const handleTransaction = async (amount: number, description: string, paymentMethod?: string, proofUrl?: string | null) => {
    if (!txModal) return;
    if (txModal.mode === 'credit') await credit.addCredit(customerId, amount, description, paymentMethod, proofUrl);
    else if (txModal.mode === 'payment') await credit.recordPayment(customerId, amount, description, paymentMethod);
    else if (txModal.mode === 'deposit') await credit.addDeposit(customerId, amount, description, paymentMethod);
    else await credit.addAdjustment(customerId, amount, description, txModal.reversesId ?? null);
  };

  const copyStatement = async () => {
    try {
      await navigator.clipboard.writeText(statementText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* */ }
  };

  const downloadStatement = () => {
    const blob = new Blob([statementText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `releve-${(customer?.full_name || 'client').replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Ouvre le relevé dans une fenêtre d'impression : l'utilisateur peut alors
  // imprimer directement ou choisir « Enregistrer au format PDF ».
  const printStatement = () => {
    const safeName = customer?.full_name || 'Client';
    // Couleur du thème du commerce (celle de son site) pour l'en-tête imprimé.
    let accent = '#111827';
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary-500').trim();
      if (raw) accent = `oklch(${raw})`;
    } catch { /* ignore */ }
    const shopName = profile.company_name || 'Mon commerce';
    const logoSrc = profile.logo_url || '';
    const contacts = [profile.phone, profile.email, [profile.address, profile.city].filter(Boolean).join(', ')]
      .filter((v) => v && String(v).trim())
      .map((v) => escapeHtml(String(v)))
      .join(' • ');
    const logoHtml = logoSrc
      ? `<img src="${escapeHtml(logoSrc)}" alt="" />`
      : `<span class="logo-ph">${escapeHtml(shopName.charAt(0).toUpperCase())}</span>`;
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    document.body.appendChild(frame);
    const frameDoc = frame.contentWindow?.document;
    if (!frameDoc) {
      document.body.removeChild(frame);
      return;
    }
    frameDoc.open();
    frameDoc.write(
      `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Relev\u00e9 - ${escapeHtml(safeName)}</title><style>` +
      `*{box-sizing:border-box;}` +
      `body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;padding:32px;position:relative;}` +
      `.wm{position:fixed;top:46%;left:50%;transform:translate(-50%,-50%) rotate(-28deg);font-size:96px;font-weight:800;color:rgba(0,0,0,0.045);white-space:nowrap;pointer-events:none;letter-spacing:2px;}` +
      `.header{display:flex;align-items:center;gap:14px;border-bottom:3px solid ${accent};padding-bottom:14px;margin-bottom:20px;position:relative;z-index:1;}` +
      `.header img{width:58px;height:58px;object-fit:contain;border-radius:10px;background:#f3f4f6;}` +
      `.logo-ph{width:58px;height:58px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800;color:#fff;background:${accent};}` +
      `.brand{font-size:19px;font-weight:800;margin:0;color:${accent};}` +
      `.contacts{font-size:11px;color:#555;margin:3px 0 0;}` +
      `h1{font-size:16px;margin:0 0 4px;position:relative;z-index:1;}` +
      `p.sub{color:#666;font-size:12px;margin:0 0 20px;position:relative;z-index:1;}` +
      `pre{white-space:pre-wrap;font-family:'Courier New',monospace;font-size:12px;line-height:1.55;border:1px solid #ddd;border-radius:8px;padding:16px;position:relative;z-index:1;background:#fff;}` +
      `</style></head><body>` +
      `<div class="wm">${escapeHtml(shopName)}</div>` +
      `<div class="header">${logoHtml}<div><p class="brand">${escapeHtml(shopName)}</p>${contacts ? `<p class="contacts">${contacts}</p>` : ''}</div></div>` +
      `<h1>Relev\u00e9 de compte</h1><p class="sub">${escapeHtml(safeName)} \u2022 ${escapeHtml(new Date().toLocaleDateString('fr-FR'))}</p>` +
      `<pre>${escapeHtml(statementText)}</pre>` +
      `</body></html>`,
    );
    frameDoc.close();
    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => {
        if (frame.parentNode) document.body.removeChild(frame);
      }, 1500);
    }, 350);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
        <p className="text-foreground-600 mb-3 text-sm">{error}</p>
        <button onClick={reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">Réessayer</button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-4 md:p-6 flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-user-unfollow-line text-4xl text-foreground-300 mb-3"></i>
        <p className="text-foreground-600 mb-4 text-sm">Client introuvable</p>
        <Link to="/dashboard/credit" className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors">Retour au carnet</Link>
      </div>
    );
  }

  const cards = [
    { label: 'Solde actuel', value: formatMoney(balance, currency), cls: balance > 0 ? 'text-primary-700' : 'text-accent-600', icon: 'ri-wallet-3-line' },
    { label: 'Plafond', value: customer.credit_limit != null ? formatMoney(customer.credit_limit, currency) : '—', cls: 'text-foreground-950', icon: 'ri-shield-line' },
    { label: 'Disponible', value: available != null ? formatMoney(available, currency) : '—', cls: 'text-foreground-950', icon: 'ri-hand-coin-line' },
    { label: 'Dernier paiement', value: lastPayment ? formatDate(lastPayment.created_at) : '—', cls: 'text-foreground-950', icon: 'ri-checkbox-circle-line' },
    { label: 'Échéance', value: dueDate ? formatDate(dueDate) : '—', cls: 'text-foreground-950', icon: 'ri-calendar-line' },
  ];

  return (
    <div className="p-4 md:p-6">
      <Link to="/dashboard/credit" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors no-underline mb-4">
        <i className="ri-arrow-left-line"></i>Carnet de crédit
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-bold flex-shrink-0">
            {customer.full_name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">{customer.full_name}</h2>
            <div className="flex items-center gap-2 flex-wrap text-xs text-foreground-500 mt-0.5">
              <span>{customer.phone || 'Sans téléphone'}</span>
              {customer.linked_user_id && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  <i className="ri-link text-[10px]"></i>Compte lié
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setTxModal({ mode: 'credit' })} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Crédit
          </button>
          <button onClick={() => setTxModal({ mode: 'payment' })} className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
            <i className="ri-hand-coin-line"></i>Paiement
          </button>
          <button onClick={() => setTxModal({ mode: 'deposit' })} className="flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer">
            <i className="ri-wallet-3-line"></i>Dépôt
          </button>
          <button onClick={() => setTxModal({ mode: 'adjustment' })} title="Ajustement" className="w-10 h-10 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-equalizer-line"></i>
          </button>
          <button onClick={() => setStatementOpen(true)} title="Relevé" className="w-10 h-10 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-file-text-line"></i>
          </button>
          <button onClick={() => setCredsOpen(true)} title="Identifiants" className="w-10 h-10 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-key-2-line"></i>
          </button>
          <button onClick={() => setQrOpen(true)} title="QR client" className="w-10 h-10 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-qr-code-line"></i>
          </button>
          <button onClick={() => setEditOpen(true)} title="Modifier" className="w-10 h-10 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-edit-line"></i>
          </button>
        </div>
      </div>

      {customer.notes && (
        <div className="mb-5 flex items-start gap-2 text-xs text-foreground-600 bg-secondary-50 border border-secondary-200/60 rounded-lg px-3 py-2.5">
          <i className="ri-sticky-note-line text-secondary-600 mt-0.5"></i>
          <span>{customer.notes}</span>
        </div>
      )}

      {/* Soldes Zifek */}
      <div className="mb-6">
        <ClientBalanceCards
          merchantId={credit.merchantId}
          clientUserId={customer.linked_user_id}
          creditCustomerId={customer.id}
          currency={currency}
          showWallet={false}
        />
      </div>

      {/* Actions Zifek partagées (crédit, dépôt magasin, dépôt portefeuille, transfert, fidélité) */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-flashlight-line"></i>
          </span>
          <div>
            <h3 className="text-sm font-bold text-foreground-950">Actions Zifek</h3>
            <p className="text-xs text-foreground-500">Crédit, dépôt solde magasin, dépôt portefeuille, transfert entre clients et fidélité.</p>
          </div>
        </div>
        <ClientZifekActions
          clientUserId={customer.linked_user_id}
          clientName={customer.full_name}
          creditCustomerId={customer.id}
          onDone={reloadSilent}
        />
      </div>

      {/* Historique dédié des transferts de solde magasin */}
      <div className="mb-6">
        <StoreTransferHistory
          merchantId={credit.merchantId}
          creditCustomerId={customer.id}
          clientUserId={customer.linked_user_id}
          currency={currency}
        />
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className="flex items-center gap-2 text-foreground-400 mb-2">
              <i className={`${c.icon} text-sm`}></i>
              <span className="text-[11px] font-medium uppercase tracking-wider">{c.label}</span>
            </div>
            <div className={`text-base md:text-lg font-bold font-heading ${c.cls}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <h3 className="text-sm font-semibold text-foreground-900 mb-3">Historique complet</h3>
      {transactions.length === 0 ? (
        <div className="py-14 text-center bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-history-line text-2xl text-foreground-300"></i>
          <p className="mt-2 text-sm text-foreground-500">Aucune opération pour ce client</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-200/70 overflow-hidden">
          {transactions.map((tx) => {
            const meta = TX_TYPE_META[tx.type];
            const sign = tx.type === 'payment' || tx.type === 'deposit' ? '-' : Number(tx.amount) < 0 ? '-' : '+';
            return (
              <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                <div className={`w-9 h-9 rounded-full ${meta.className} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${meta.icon} text-base`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground-900 truncate">
                    {meta.label}{tx.description ? ` · ${tx.description}` : ''}
                    {tx.reverses_transaction_id ? <span className="text-[10px] text-secondary-600 ml-1.5">(correction)</span> : null}
                  </div>
                  <div className="text-[11px] text-foreground-400">
                    {formatDateTime(tx.created_at)}{tx.payment_method ? ` · ${tx.payment_method}` : ''}
                  </div>
                </div>
                <div className={`text-sm font-bold flex-shrink-0 ${tx.type === 'payment' || tx.type === 'deposit' ? 'text-accent-600' : tx.type === 'credit' ? 'text-primary-700' : 'text-secondary-600'}`}>
                  {sign}{formatMoney(Math.abs(Number(tx.amount)), currency)}
                </div>
                {tx.proof_url && (
                  <a
                    href={tx.proof_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Voir la photo de preuve"
                    className="w-9 h-9 rounded-md overflow-hidden border border-background-200/70 flex-shrink-0 no-underline"
                  >
                    <img src={tx.proof_url} alt="Preuve" className="w-full h-full object-cover object-top" />
                  </a>
                )}
                <button onClick={() => setTxModal({ mode: 'adjustment', reversesId: tx.id })} title="Corriger"
                  className="w-8 h-8 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-arrow-go-back-line text-sm"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {txModal && (
        <TransactionModal
          mode={txModal.mode} customer={customer} currency={currency} balance={balance}
          onClose={() => setTxModal(null)} onSubmit={handleTransaction}
        />
      )}

      {editOpen && (
        <CustomerFormModal
          customer={customer} currency={currency} onClose={() => setEditOpen(false)}
          onSubmit={async (data) => { await credit.updateCustomer(customer.id, data); }}
        />
      )}

      {qrOpen && (
        <QrModal title={customer.full_name} subtitle="QR client" data={buildCustomerQrData(customer.id)} onClose={() => setQrOpen(false)} />
      )}

      {credsOpen && (
        <CredentialsModal
          customerName={customer.full_name}
          username={customer.username}
          password={customer.temp_password}
          onRegenerate={async () => {
            const next = await credit.regenerateCredentials(customer.id);
            return next;
          }}
          onClose={() => setCredsOpen(false)}
        />
      )}

      {statementOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setStatementOpen(false)} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-lg bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[88vh] flex flex-col">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2"><i className="ri-file-text-line text-primary-500"></i>Relevé de compte</h3>
                <button onClick={() => setStatementOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="p-5 overflow-y-auto">
                <pre className="text-xs text-foreground-700 whitespace-pre-wrap font-mono bg-background-100 rounded-lg p-3">{statementText}</pre>
              </div>
              <div className="p-4 border-t border-background-200/70 grid grid-cols-1 sm:grid-cols-3 gap-2 flex-shrink-0">
                <button onClick={copyStatement} className="h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>{copied ? 'Copié !' : 'Copier'}
                </button>
                <button onClick={downloadStatement} className="h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                  <i className="ri-download-2-line"></i>Télécharger
                </button>
                <button onClick={printStatement} className="h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                  <i className="ri-printer-line"></i>Imprimer / PDF
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}